import React, { useMemo, useCallback, forwardRef, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Column from '../column/Column';
import useColumns from './useColumns';
import { usePlaceContext } from '../PlaceContext';
import { useDragAndDrop } from '../drag_and_drop/useDragAndDrop';
import '../../../styles/components/itinerary/Columns.css';
import useApi from '../../../Api';
import { useAuth } from '../../../AuthContext';

const Columns = forwardRef(({ initialColumns, selectedItinerary = null, onColumnsChange, readOnly }, ref) => {
  const { stateRef, saveItineraryColumns, updatePlaceNotes } = usePlaceContext();
  const { columns, addColumn, deleteColumn, setColumnsToSave, setEditingColumnIndex, setColumns } = useColumns(initialColumns, saveItineraryColumns, readOnly);
  const apiClient = useApi();
  const { user, token } = useAuth();
  const dateUpdateTimeout = useRef(null);
  const { handleDragOver, handleDragLeave, handleRemoveWidget, handleDrop, setDragData, scrollRef } = useDragAndDrop(setColumns, setColumnsToSave, null, null, selectedItinerary, null);

  useEffect(() => {
    if (onColumnsChange) onColumnsChange();
  }, [columns, onColumnsChange]);

  useEffect(() => {
    if (ref && typeof ref === 'object') {
      scrollRef.current = ref.current;
    }
  }, [ref, scrollRef]);

  const handleColumnTitleChange = useCallback((index, newTitle, columnId) => {
    if (newTitle.length <= 25) {
      setColumns(prevColumns => {
        const updated = prevColumns.map((col, i) => i === index ? { ...col, title: newTitle } : col);
        setColumnsToSave(updated);
        return updated;
      });
    }
    const itineraryId = stateRef.current.selectedItinerary.id;
    if (dateUpdateTimeout.current) clearTimeout(dateUpdateTimeout.current);
    dateUpdateTimeout.current = setTimeout(async () => {
      try {
        await apiClient.patch(`/columns/${columnId}/rename?itinerary_id=${itineraryId}`, { name: newTitle }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error updating column title:', error);
      }
    }, 500);
  }, [setColumns, setColumnsToSave, stateRef, apiClient, token]);

  const handleColumnBlur = useCallback((index) => {
    setColumns(prev => {
      const updated = prev.map((col, i) => i === index ? { ...col, isEditing: false } : col);
      setColumnsToSave(updated);
      return updated;
    });
  }, [setColumns, setColumnsToSave]);

  const handleColumnKeyDown = useCallback((e, index) => {
    if (e.key === 'Enter') handleColumnBlur(index);
  }, [handleColumnBlur]);

  const startColumnEditing = useCallback((index) => {
    setColumns(prev => prev.map((col, i) => i === index ? { ...col, isEditing: true } : col));
    setEditingColumnIndex(index);
  }, [setColumns, setEditingColumnIndex]);

  const handleDateChange = useCallback((index, date) => {
    setColumns(prev => {
      const updated = prev.map((col, i) => i === index ? { ...col, date } : col);
      setColumnsToSave(updated);
      return updated;
    });
    if (dateUpdateTimeout.current) clearTimeout(dateUpdateTimeout.current);
    dateUpdateTimeout.current = setTimeout(async () => {
      const itineraryId = stateRef.current.selectedItinerary.id;
      const columnId = columns[index].id;
      try {
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        await apiClient.patch(`/itineraries/${itineraryId}/columns/${columnId}/date/update`, { date: formattedDate }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error updating column date:', error);
      }
    }, 500);
  }, [setColumns, setColumnsToSave, stateRef, apiClient, columns, token]);

  const handleNotesChange = useCallback((columnId, placeId, newNotes) => {
    setColumns(prev => {
      const updated = prev.map(col => col.id === columnId ? { ...col, items: col.items.map(item => item.id === placeId ? { ...item, notes: newNotes } : item) } : col);
      setColumnsToSave(updated);
      return updated;
    });
    updatePlaceNotes(placeId, newNotes);
  }, [setColumns, setColumnsToSave, updatePlaceNotes]);

  const renderedColumns = useMemo(() =>
    columns.map((column, index) => (
      <Column
        key={column.id}
        column={column}
        index={index}
        handleDrop={handleDrop}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleColumnTitleChange={handleColumnTitleChange}
        handleColumnBlur={handleColumnBlur}
        handleColumnKeyDown={handleColumnKeyDown}
        startColumnEditing={startColumnEditing}
        setDragData={setDragData}
        handleRemoveWidget={handleRemoveWidget}
        deleteColumn={deleteColumn}
        handleDateChange={handleDateChange}
        handleNotesChange={handleNotesChange}
        readOnly={readOnly}
      />
    )), [columns, handleDrop, handleDragOver, handleDragLeave, handleColumnTitleChange, handleColumnBlur, handleColumnKeyDown, startColumnEditing, setDragData, handleRemoveWidget, deleteColumn, handleDateChange, handleNotesChange, readOnly]
  );

  return (
    <div className="columns-wrapper">
      <div className="columns-container" ref={ref}>
        {renderedColumns}
        {!readOnly && (
          <button className={`add-column-button ${columns.length >= 10 ? 'at-limit' : ''}`} onClick={addColumn} aria-label="Add new column">
            Add Column
          </button>
        )}
      </div>
    </div>
  );
});

Columns.propTypes = {
  initialColumns: PropTypes.array.isRequired,
  selectedItinerary: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    columns: PropTypes.array,
  }),
  onColumnsChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default Columns;
